import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiServiceErr, axiosApi } from "./apiService";

export interface CommonValues {
  key?: string;
  indicator?: string;
  measure?: number;
  unit?: string;
}

export interface CycleStages {
  cultivationImpact?: string;
  processImpact?: string;
  transportImpact?: string;
  storageImpact?: string;
}

interface ProcessedBillOfMaterialSummary {
  productDetail: any;
  productName: string;
  totalImpact: CommonValues[];
  climateChange: CommonValues;
  cycleStages: CycleStages;
  errors: string[];
  warnings: string[];
}

interface ProcessedBillOfMaterialSummaryOptions {
  pageNumber?: number;
  pageSize?: number;
  ingredientId?: string;
  supplierId?: string;
  isSupplier?: boolean;
}

export interface BillOfMaterialSummary {
  _id: string;
  ingredientId: string;
  ingredientName: string;
  supplierId: string;
  supplierName: string;
  tenantId: string;
  ingredients: Record<string, any>[];
  cultivation: Record<string, any>[];
  storages: Record<string, any>[];
  transportation: Record<string, any>[];
  processes: Record<string, any>[];
  totalImpact: Record<string, any>;
  totalImpactPercentages: Record<string, any>;
  errors?: Record<string, any>;
  warnings?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBillOfMaterialDto {
  ingredientId: string;
  supplierId: string;
  ingredientName: string;
  supplierName: string;
  totalImpact: Record<string, any>;
}

export const useGetBillOfMaterialSummary = ({
  ingredientId,
  supplierId,
  isSupplier = true
}: ProcessedBillOfMaterialSummaryOptions) =>
  useQuery<ProcessedBillOfMaterialSummary, ApiServiceErr>(
    [ingredientId, supplierId],
    async () => {
      const response = await axiosApi.get("/bill-of-material-summary/by-id", {
        params: { ingredientId, supplierId }
      });
      return response.data;
    },
    {
      enabled: !!ingredientId && !!supplierId && !isSupplier
    }
  );

export const useGetBillOfMaterialSummariesForNodes = ({
  ingredientId,
  supplierId
}: ProcessedBillOfMaterialSummaryOptions) =>
  useQuery<any, ApiServiceErr>(
    [ingredientId, supplierId, "useGetBillOfMaterialSummariesForNodes"],
    async () => {
      const response = await axiosApi.get("/bill-of-material-summary/node-summary-impacts", {
        params: { ingredientId, supplierId }
      });
      return response.data || {};
    },
    {
      enabled: !!ingredientId && !!supplierId
    }
  );

export const useGetBillOfMaterialSummaryHistory = ({
  pageNumber,
  pageSize,
  ingredientId,
  supplierId,
  isSupplier = true
}: ProcessedBillOfMaterialSummaryOptions) =>
  useQuery<any, ApiServiceErr>(
    [ingredientId, supplierId, pageNumber, pageSize, "useGetBillOfMaterialSummaryHistory"],
    async () => {
      const response = await axiosApi.get("/bill-of-material-summary/history", {
        params: { pageNumber, pageSize, ingredientId, supplierId }
      });

      return response.data;
    },
    {
      enabled: !!ingredientId && !!supplierId && !isSupplier
    }
  );

export const useGetLatestBillOfMaterialSummary = ({
  ingredientId,
  supplierId
}: ProcessedBillOfMaterialSummaryOptions) =>
  useQuery<BillOfMaterialSummary, ApiServiceErr>(
    [ingredientId, supplierId, "latest-bill-of-material"],
    async () => {
      const response = await axiosApi.get("/bill-of-material-summary/latest", {
        params: { ingredientId, supplierId }
      });
      console.log("🚀 ~ useGetLatestBillOfMaterialSummary ~ response:", response);
      return response.data;
    },
    {
      enabled: !!ingredientId && !!supplierId
    }
  );

export const useCreateBillOfMaterial = () =>
  useMutation<BillOfMaterialSummary, ApiServiceErr, CreateBillOfMaterialDto>(
    async (billOfMaterialData: CreateBillOfMaterialDto) => {
      const response = await axiosApi.post("/bill-of-material-summary", billOfMaterialData);
      return response.data;
    }
  );
